/**
 * Attribuer un expéditeur à beaucoup de dossiers d'un coup.
 *
 * Deux difficultés, mesurées sur un vrai cabinet (14/09/2026) : un responsable
 * suit jusqu'à 111 dossiers alors qu'une page de la liste en montre au plus
 * 100 (plafond de l'API) ; et l'API n'attribue qu'un dossier par appel.
 */

/** Ce qu'il faut d'un dossier pour l'attribuer et, en cas d'échec, le nommer. */
export interface DossierVise {
  uuid: string;
  name?: string;
}

export interface PageDeDossiers {
  data: DossierVise[];
  totalPages: number;
}

/**
 * Tous les dossiers qui répondent aux filtres en cours, page après page, avec
 * leur nom. `lirePage` reçoit le numéro de page (à partir de 1).
 */
export async function collecterDossiersNommesDuFiltre(
  lirePage: (page: number) => Promise<PageDeDossiers>
): Promise<DossierVise[]> {
  const parUuid = new Map<string, DossierVise>();
  let page = 1;
  let totalPages = 1;

  do {
    const reponse = await lirePage(page);
    // Un dossier peut glisser d'une page à l'autre si la liste bouge pendant
    // la lecture : on ne l'attribue qu'une fois.
    for (const dossier of reponse.data) {
      if (!parUuid.has(dossier.uuid)) {
        parUuid.set(dossier.uuid, { uuid: dossier.uuid, name: dossier.name });
      }
    }
    totalPages = reponse.totalPages || 1;
    page += 1;
  } while (page <= totalPages);

  return [...parUuid.values()];
}

/** Les seuls identifiants, pour qui n'a pas besoin des noms. */
export async function collecterDossiersDuFiltre(
  lirePage: (page: number) => Promise<PageDeDossiers>
): Promise<string[]> {
  return (await collecterDossiersNommesDuFiltre(lirePage)).map(d => d.uuid);
}

export interface BilanAttribution {
  reussis: number;
  /** Identifiants des dossiers en échec (voir `nommerEchecs` pour l'écran). */
  echecs: string[];
  /** Ce que l'API a répondu pour chaque échec, quand elle l'a dit. */
  raisons: Record<string, string>;
}

/** Le message utile d'une erreur d'appel (réponse de l'API, sinon rien). */
function raisonDe(erreur: unknown): string | undefined {
  const e = erreur as {
    response?: { data?: { message?: unknown } };
  };
  const message = e?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message) && typeof message[0] === 'string') {
    return message[0];
  }
  return undefined;
}

/**
 * Les dossiers en échec, nommés pour l'écran : le compte seul ne dit pas au
 * cabinet lesquels reprendre (relevé QA du 16/09/2026). Sans nom connu, un
 * dossier est désigné par le début de son identifiant.
 */
export function nommerEchecs(
  echecs: string[],
  dossiers: DossierVise[],
  raisons: Record<string, string> = {}
): string[] {
  const noms = new Map(dossiers.map(d => [d.uuid, d.name] as const));
  return echecs.map(uuid => {
    const nom = noms.get(uuid) || `dossier ${uuid.slice(0, 8)}`;
    return raisons[uuid] ? `${nom} — ${raisons[uuid]}` : nom;
  });
}

/**
 * Applique `attribuer` à chaque dossier, quelques-uns à la fois pour ne pas
 * saturer l'API. Un échec n'arrête pas les autres : il est compté.
 */
export async function attribuerParLots(
  dossiers: string[],
  attribuer: (dossierUuid: string) => Promise<unknown>,
  onProgression?: (faits: number) => void,
  simultanes = 5
): Promise<BilanAttribution> {
  const bilan: BilanAttribution = { reussis: 0, echecs: [], raisons: {} };
  let faits = 0;
  let prochain = 0;

  const ouvrier = async () => {
    while (prochain < dossiers.length) {
      const dossierUuid = dossiers[prochain];
      prochain += 1;
      try {
        await attribuer(dossierUuid);
        bilan.reussis += 1;
      } catch (erreur) {
        bilan.echecs.push(dossierUuid);
        const raison = raisonDe(erreur);
        if (raison) bilan.raisons[dossierUuid] = raison;
      }
      faits += 1;
      onProgression?.(faits);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(simultanes, dossiers.length) }, ouvrier)
  );

  return bilan;
}
